import { ChangeEvent, FC, useCallback, useEffect, useState } from "react";
import { TableCell, TableRow, Typography } from "@mui/material";
import { useDebounce } from "rooks";
import { ShareAmount } from "@/services/cloudchipr.api";
import { useAppSelector } from "@/store/hooks";
import { dimensionCategoriesIdsWithNamesSelector } from "@/store/dimensions/selectors/slice-data/categories/dimensionCategoriesIdsWithNamesSelector";
import { NumberField } from "@/components/common/NumberField";

interface DimensionCostAllocationCardSharedAmountsTableManualRowProps {
  shareAmount: ShareAmount;
  amountsSum: number;
  onAmountChange(categoryId: string, shareAmount: number): void;
}

export const DimensionCostAllocationCardSharedAmountsTableManualRow: FC<
  DimensionCostAllocationCardSharedAmountsTableManualRowProps
> = ({ shareAmount, onAmountChange, amountsSum }) => {
  const categoryId = shareAmount.category_id;
  const categoryNamesWithIds = useAppSelector(
    dimensionCategoriesIdsWithNamesSelector,
  );
  const [amount, setAmount] = useState(0);

  const onAmountChangeDebounced = useDebounce(onAmountChange, 500);

  const amountChangeHandler = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = +event.target.value;

      setAmount(value);
      onAmountChangeDebounced(categoryId, value);
    },
    [categoryId, onAmountChangeDebounced],
  );

  useEffect(() => {
    setAmount(shareAmount.share_amount);
  }, [shareAmount.share_amount]);

  const error = amountsSum > 100;

  return (
    <TableRow>
      <TableCell sx={{ py: 1, width: "50%" }}>
        {categoryNamesWithIds[categoryId]}
      </TableCell>

      <TableCell sx={{ py: 1, width: "50%" }}>
        <NumberField
          error={error}
          helperText={error ? `Amounts sum can't be higher than 100` : ""}
          onChange={amountChangeHandler}
          variant="outlined"
          value={amount.toString()}
          size="xsmall"
          minValue={0}
          InputProps={{
            endAdornment: (
              <Typography ml={0.5} color={error ? "error" : "text.secondary"}>
                %
              </Typography>
            ),
          }}
        />
      </TableCell>
    </TableRow>
  );
};
